import {
  createChapter,
  deleteChapter,
  updateChapter,
} from '@/data/services/chapterServices';
import Chapter, { TempChapter } from '@/models/Chapter';
import { buildChangedObject } from '../buildChangedObject';
import { separeArrayStudioActions } from '@/pages/admin/courses/studio/info/CourseStudioInfo.hooks';
import { TempLesson } from '@/models/Lesson';

/* Chapters in lesson */
export async function performDeleteChapters(
  chapters: TempChapter[],
  lessonId: number,
  bookId?: number,
): Promise<void> {
  for (const chapter of chapters) {
    if (chapter.id && bookId) {
      await deleteChapter({
        lessonId,
        bookType: 'STUDENT',
        bookId,
        chapterId: chapter.id,
      });
    }
  }
}
export async function performUpdateChapter(
  currentChapter: TempChapter,
  newChapter: TempChapter,
  lessonId: number,
  bookId?: number,
): Promise<TempChapter> {
  if (!bookId) return newChapter;

  const changes = buildChangedObject(currentChapter, newChapter, {
    ignoreFields: ['tempId', 'items', 'book'],
  });
  if (Object.keys(changes).length === 0) return newChapter;

  const updatedChapter = await updateChapter({
    params: { lessonId, bookType: 'STUDENT', bookId, chapterId: newChapter.id },
    body: changes,
  });

  updatedChapter.items = newChapter.items;
  return updatedChapter;
}
export async function performUpdateChapters(
  currentChapters: TempChapter[],
  newChapters: TempChapter[],
  lessonId: number,
  bookId?: number,
): Promise<TempChapter[]> {
  if (!bookId) return newChapters;

  const { arrayToDelete, arrayToUpdate, arrayToCreate } =
    separeArrayStudioActions(currentChapters, newChapters);

  await performDeleteChapters(arrayToDelete, lessonId, bookId);

  const currentChaptersMap = new Map(currentChapters.map(c => [c.id, c]));

  const updatedChapters: TempChapter[] = [];
  for (const newChapter of arrayToUpdate) {
    const currentChapter = currentChaptersMap.get(newChapter.id);
    if (!currentChapter) continue;
    const updatedChapter = await performUpdateChapter(
      currentChapter,
      newChapter,
      lessonId,
      bookId,
    );

    updatedChapters.push(updatedChapter);
  }

  const createdChapters = await performCreateChapter({
    chapters: arrayToCreate,
    lessonId,
    bookId,
  });

  return [...updatedChapters, ...createdChapters];
}
type PerformeCreateArgs = {
  chapters?: TempChapter[];
  lessonId: number;
  bookId?: number;
};

export async function performCreateChapter({
  chapters = [],
  lessonId,
  bookId,
}: PerformeCreateArgs) {
  const createdChapters: Chapter[] = [];

  for (const chapter of chapters) {
    if (!bookId) return [];
    const createdChapter = await createChapter(
      {
        lessonId,
        bookType: 'STUDENT',
        bookId,
      },
      { title: chapter.title, content: '' },
    );

    createdChapter.items = chapter.items;
    createdChapters.push(createdChapter);
  }

  return createdChapters;
}
export async function performCreateChapters(lessons: TempLesson[]) {
  const createdLessons: TempLesson[] = [];

  for (const lesson of lessons) {
    lesson.chapters = await performCreateChapter({
      chapters: lesson.chapters,
      lessonId: lesson.id,
      bookId: lesson.book?.id,
    });
    createdLessons.push(lesson);
  }

  return createdLessons;
}

export async function performUpdateLessonChapters(
  currentLessons: TempLesson[],
  newLessons: TempLesson[],
): Promise<TempLesson[]> {
  const currentLessonsMap = new Map(currentLessons.map(l => [l.id, l]));
  const updatedLessons: TempLesson[] = [];

  for (const newLesson of newLessons) {
    const currentLesson = currentLessonsMap.get(newLesson.id);

    const updatedChapters = await performUpdateChapters(
      currentLesson?.chapters || [],
      newLesson.chapters || [],
      newLesson.id,
      newLesson.book?.id,
    );

    newLesson.chapters = updatedChapters;
    updatedLessons.push(newLesson);
  }

  return updatedLessons;
} /* Create chapters */
